/**
 * Reports Service
 * Service for analytics and statistics
 */

import { api } from './api'

export interface DailyEarnings {
  date: string // YYYY-MM-DD format
  earnings: number
  count: number // number of loans
}

export interface CurrencyStats {
  currency: string
  totalLent: number
  totalEarned: number
  activeBots: number
  avgDailyRate: number
}

export interface OverallStats {
  totalEarnings: number
  totalLent: number
  activeBots: number
  totalBots: number
  averageAPR: number
}

export interface BotPerformance {
  id: number
  name: string
  currency: string
  status: string
  totalLent: number
  totalEarned: number
  roi: number
  daysActive: number
  earningsPerDay: number
  lastCycleAt: number | null
}

/**
 * Reports Service
 */
export const reportsService = {
  /**
   * Get overall statistics from all bots
   */
  async getOverallStats(): Promise<OverallStats> {
    const response = await api.get<{ success: boolean; data: OverallStats }>('/reports/overall')
    return response.data
  },

  /**
   * Get statistics by currency
   */
  async getCurrencyStats(): Promise<CurrencyStats[]> {
    const response = await api.get<{ success: boolean; data: CurrencyStats[] }>('/reports/currency')
    return response.data
  },

  /**
   * Get daily earnings breakdown for charts
   */
  async getDailyEarnings(days: number = 30): Promise<DailyEarnings[]> {
    const response = await api.get<{ success: boolean; data: DailyEarnings[] }>('/reports/daily', {
      days: days.toString(),
    })
    return response.data
  },

  /**
   * Get bot performance comparison
   */
  async getBotPerformance(): Promise<
    Array<{
      name: string
      currency: string
      earned: number
      lent: number
      roi: number
      status: string
    }>
  > {
    const response = await api.get<{ success: boolean; data: BotPerformance[] }>('/reports/performance')

    // Map to expected format
    return response.data.map((bot) => ({
      name: bot.name,
      currency: bot.currency,
      earned: bot.totalEarned,
      lent: bot.totalLent,
      roi: bot.roi,
      status: bot.status,
    }))
  },

  /**
   * Export report as CSV
   */
  async exportCSV(type: 'earnings' | 'loans' | 'performance', days: number = 30): Promise<Blob> {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/reports/export/csv?type=${type}&days=${days}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      }
    )

    if (!response.ok) {
      throw new Error('Failed to export CSV')
    }

    return response.blob()
  },
}
